/* ============================================================
   Minty theme — storefront interactivity
   AJAX cart + drawer, variant picker, qty steppers, gallery.
   Uses Shopify's /cart AJAX API (no external deps).
   ============================================================ */
(function () {
  "use strict";

  const money = (cents) => {
    const fmt = (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD";
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency", currency: fmt
      }).format(cents / 100);
    } catch (e) {
      return "$" + (cents / 100).toFixed(2);
    }
  };

  /* ---------- Cart drawer ---------- */
  const drawer = document.querySelector("[data-cart-drawer]");
  const openDrawer = () => { if (drawer) { drawer.setAttribute("aria-hidden", "false"); document.body.style.overflow = "hidden"; } };
  const closeDrawer = () => { if (drawer) { drawer.setAttribute("aria-hidden", "true"); document.body.style.overflow = ""; } };

  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-cart-open]")) { e.preventDefault(); openDrawer(); refreshCart(); }
    if (e.target.closest("[data-cart-close]")) { e.preventDefault(); closeDrawer(); }
  });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeDrawer(); });

  /* ---------- Update header count + drawer markup ---------- */
  function setCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return res.json();
  }

  function renderDrawer(cart) {
    const body = drawer && drawer.querySelector("[data-drawer-body]");
    const foot = drawer && drawer.querySelector("[data-drawer-foot]");
    if (!body) return;
    if (cart.item_count === 0) {
      body.innerHTML = '<div class="empty-state"><h3>Your cart is empty</h3><p>Nothing here yet - let\'s find you some swag.</p><a class="btn btn--primary" href="/collections/all">Shop swag</a></div>';
      if (foot) foot.hidden = true;
      return;
    }
    if (foot) foot.hidden = false;
    body.innerHTML = cart.items.map((item) => `
      <div class="cart-line" data-line-key="${item.key}">
        <a class="cart-line__media" href="${item.url}">${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ""}</a>
        <div>
          <p class="cart-line__title"><a href="${item.url}">${item.product_title}</a></p>
          ${item.variant_title && item.variant_title !== "Default Title" ? `<p class="cart-line__variant">${item.variant_title}</p>` : ""}
          <div class="qty-selector" data-qty data-line-key="${item.key}">
            <button type="button" data-qty-minus aria-label="Decrease">&minus;</button>
            <input type="number" min="0" value="${item.quantity}" data-qty-input aria-label="Quantity">
            <button type="button" data-qty-plus aria-label="Increase">+</button>
          </div>
        </div>
        <div class="cart-line__right">
          <span class="price">${money(item.final_line_price)}</span>
          <button class="cart-line__remove" type="button" data-line-remove="${item.key}">Remove</button>
        </div>
      </div>`).join("");
    const totalEl = drawer.querySelector("[data-drawer-total]");
    if (totalEl) totalEl.textContent = money(cart.total_price);
  }

  async function refreshCart() {
    try {
      const cart = await getCart();
      setCartCount(cart.item_count);
      if (drawer) renderDrawer(cart);
      // re-render full cart page if present
      if (document.querySelector("[data-cart-page]") && cart) {
        window.location.reload();
      }
    } catch (e) { console.error("Minty cart refresh failed", e); }
  }

  async function changeLine(key, quantity) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity })
    });
    const cart = await res.json();
    setCartCount(cart.item_count);
    if (drawer) renderDrawer(cart);
    if (document.querySelector("[data-cart-page]")) window.location.reload();
    return cart;
  }

  /* ---------- Add to cart (AJAX) ---------- */
  document.addEventListener("submit", async (e) => {
    const form = e.target.closest(".product-form");
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const label = btn ? btn.textContent : "";
    if (btn) { btn.setAttribute("aria-disabled", "true"); btn.textContent = "Adding\u2026"; }
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          id: form.querySelector('[name="id"]').value,
          quantity: parseInt(form.querySelector('[name="quantity"]') ? form.querySelector('[name="quantity"]').value : "1", 10)
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || "Could not add to cart");
      }
      await refreshCart();
      openDrawer();
    } catch (err) {
      const notice = form.querySelector("[data-form-error]");
      if (notice) { notice.textContent = err.message; notice.hidden = false; }
    } finally {
      if (btn) { btn.removeAttribute("aria-disabled"); btn.textContent = label; }
    }
  });

  /* ---------- Qty steppers + remove (delegated) ---------- */
  document.addEventListener("click", (e) => {
    const wrap = e.target.closest("[data-qty]");
    if (wrap) {
      const input = wrap.querySelector("[data-qty-input]");
      const key = wrap.getAttribute("data-line-key");
      let val = parseInt(input.value || "1", 10);
      if (e.target.closest("[data-qty-plus]")) val += 1;
      if (e.target.closest("[data-qty-minus]")) val = Math.max(parseInt(input.min || "0", 10), val - 1);
      if (e.target.closest("[data-qty-plus]") || e.target.closest("[data-qty-minus]")) {
        input.value = val;
        if (key) changeLine(key, val);
      }
    }
    const remove = e.target.closest("[data-line-remove]");
    if (remove) { e.preventDefault(); changeLine(remove.getAttribute("data-line-remove"), 0); }
  });
  document.addEventListener("change", (e) => {
    const wrap = e.target.closest("[data-qty][data-line-key]");
    if (wrap && e.target.matches("[data-qty-input]")) {
      changeLine(wrap.getAttribute("data-line-key"), Math.max(0, parseInt(e.target.value || "0", 10)));
    }
  });

  /* ---------- Variant picker (enhancement only) ----------
     The native <select name="id"> already submits the correct variant with
     no JS. This just updates the displayed price/label and the URL. Any
     failure is caught and never affects add-to-cart. */
  document.querySelectorAll(".product-form").forEach((form) => {
    const data = form.querySelector("[data-variants-json]");
    const select = form.querySelector("[data-variant-select]");
    if (!data || !select) return;
    let variants = [];
    try { variants = JSON.parse(data.textContent); } catch (e) { return; }
    const priceEl = document.querySelector("[data-product-price]");
    const submit = form.querySelector('[type="submit"][name="add"]');

    function update() {
      const id = parseInt(select.value, 10);
      const variant = variants.find((v) => v.id === id);
      if (!variant) return;
      if (submit) {
        if (variant.available) { submit.disabled = false; submit.textContent = submit.dataset.addLabel || "Add to cart"; }
        else { submit.disabled = true; submit.textContent = "Sold out"; }
      }
      if (priceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceEl.innerHTML = `<span class="price__sale">${money(variant.price)}</span> <s class="price__compare">${money(variant.compare_at_price)}</s>`;
        } else {
          priceEl.innerHTML = `<span>${money(variant.price)}</span>`;
        }
      }
      const url = new URL(window.location);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    }
    select.addEventListener("change", update);
  });

  /* ---------- Product gallery ---------- */
  document.querySelectorAll("[data-gallery]").forEach((gallery) => {
    const main = gallery.querySelector("[data-gallery-main] img");
    gallery.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
      thumb.addEventListener("click", () => {
        if (main) { main.src = thumb.dataset.full; main.srcset = ""; }
        gallery.querySelectorAll("[data-gallery-thumb]").forEach((t) => t.classList.remove("is-active"));
        thumb.classList.add("is-active");
      });
    });
  });

  /* ---------- Mobile nav ---------- */
  const navToggle = document.querySelector("[data-nav-toggle]");
  if (navToggle) {
    navToggle.addEventListener("click", () => {
      const nav = document.querySelector("[data-mobile-nav]");
      if (nav) nav.hidden = !nav.hidden;
    });
  }

  /* ---------- Product recommendations (lazy) ---------- */
  document.querySelectorAll("[data-recs]").forEach((el) => {
    const url = el.getAttribute("data-url");
    if (!url) { el.remove(); return; }
    fetch(url)
      .then((r) => (r.ok ? r.text() : null))
      .then((html) => {
        if (!html) { el.remove(); return; }
        const doc = new DOMParser().parseFromString(html, "text/html");
        // Inject ONLY our uniquely-marked recommendations container. The
        // endpoint returns the store 404 page when a product has no related
        // products; that page has no [data-recs-inner], so nothing is injected.
        const inner = doc.querySelector("[data-recs-inner]");
        if (inner && inner.querySelector(".collection-grid .product-card")) {
          el.replaceWith(inner);
        } else {
          el.remove();
        }
      })
      .catch(() => { el.remove(); });
  });

  /* init count on load */
  getCart().then((c) => setCartCount(c.item_count)).catch(() => {});
})();
